/**
 * WidgetCard — WhatsApp widget settings, shown on the Integrations page next to
 * the WhatsApp connection (only once paired). Reads/writes the per-client widget
 * config via /api/clients/{id}/widget-config. Mirrors the ProfileSection
 * query/PATCH/invalidate pattern + the shadcn form conventions.
 *
 * Badge lock: on Free the "Powered by HeyMotek" badge is mandatory — the toggle
 * is disabled and the backend forces it on regardless of what's sent.
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';

import { apiRequest, ApiError } from '@/lib/api';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SkeletonBox, SkeletonGroup } from '@/components/Skeleton';
import { ErrorState } from '@/components/ErrorState';
import { cn } from '@/lib/utils';
import { WhatsAppGlyphButton } from '@/components/WhatsAppGlyphButton';

// WhatsApp green is the default; the rest are a small brand-sane palette. The
// native picker (the dashed "+" swatch) covers anything else.
const DEFAULT_COLOR = '#25D366';
const PRESET_COLORS = [
  '#25D366',
  '#128C7E',
  '#2563EB',
  '#F43F5E',
  '#111827',
  '#64748B',
];

interface WidgetConfig {
  enabled: boolean;
  position: 'bottom-right' | 'bottom-left';
  color: string;
  message: string;
  offset: number;
  show_badge: boolean;
  number: string;
  plan_tier: string;
}

type Draft = Pick<WidgetConfig, 'enabled' | 'position' | 'color' | 'message' | 'show_badge'>;

export function WidgetCard({ clientId, isFree }: { clientId: string; isFree: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['client', clientId, 'widget-config'],
    queryFn: () => apiRequest<WidgetConfig>(`/clients/${clientId}/widget-config`),
  });

  return (
    <section className="rounded-3xl border border-dark/5 bg-card p-6 shadow-card sm:p-8">
      <div className="mb-4 flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-extrabold text-dark sm:text-xl">WhatsApp button</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            A floating WhatsApp button we render on your site — so every click is
            captured, even on pages where you don't have your own button.
          </p>
        </div>
        {query.data?.enabled && (
          <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-green-700">
            On
          </span>
        )}
      </div>

      {query.isLoading && (
        <SkeletonGroup className="space-y-3">
          <SkeletonBox className="h-4 w-48" />
          <SkeletonBox className="h-36 w-full rounded-2xl" />
        </SkeletonGroup>
      )}

      {query.isError && (
        <ErrorState
          title="Couldn't load your widget settings"
          onRetry={() => query.refetch()}
        />
      )}

      {query.data &&
        (query.data.number ? (
          <WidgetEditor
            clientId={clientId}
            isFree={isFree}
            data={query.data}
            onSaved={() =>
              queryClient.invalidateQueries({
                queryKey: ['client', clientId, 'widget-config'],
              })
            }
          />
        ) : (
          <p className="rounded-2xl bg-bg-tint p-4 text-sm text-muted-foreground">
            Connect your WhatsApp number above to turn on the floating button.
          </p>
        ))}
    </section>
  );
}

function WidgetEditor({
  clientId,
  isFree,
  data,
  onSaved,
}: {
  clientId: string;
  isFree: boolean;
  data: WidgetConfig;
  onSaved: () => void;
}) {
  const initial: Draft = {
    enabled: data.enabled,
    position: data.position,
    color: data.color,
    message: data.message,
    show_badge: data.show_badge,
  };
  const [draft, setDraft] = useState<Draft>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-sync the draft when the upstream config changes (e.g. after a save +
  // refetch). Keyed on the primitive fields so an idle refetch with identical
  // data doesn't clobber an in-progress edit.
  useEffect(() => {
    setDraft({
      enabled: data.enabled,
      position: data.position,
      color: data.color,
      message: data.message,
      show_badge: data.show_badge,
    });
  }, [data.enabled, data.position, data.color, data.message, data.show_badge]);

  const dirty = (Object.keys(initial) as (keyof Draft)[]).some(
    (k) => draft[k] !== initial[k],
  );

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const body: Partial<Draft> = { ...draft };
      // Free can't change the badge — don't even send it (backend enforces too).
      if (isFree) delete body.show_badge;
      await apiRequest(`/clients/${clientId}/widget-config`, {
        method: 'PATCH',
        body,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail || e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  const badgeShown = isFree ? true : draft.show_badge;

  return (
    <div className="space-y-5">
      <div className="grid gap-6 sm:grid-cols-2">
        {/* Settings */}
        <div className="space-y-4">
          <label className="flex cursor-pointer items-start gap-3">
            <Checkbox
              checked={draft.enabled}
              onCheckedChange={(c) => set('enabled', c === true)}
              className="mt-0.5"
            />
            <span className="flex-1">
              <span className="block text-sm font-semibold text-dark">
                Show the button on my site
              </span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                Links to {data.number}.
              </span>
            </span>
          </label>

          <div className="space-y-1.5">
            <Label>Position</Label>
            <Select
              value={draft.position}
              onValueChange={(v) => set('position', v as Draft['position'])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bottom-right">Bottom right</SelectItem>
                <SelectItem value="bottom-left">Bottom left</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Button color</Label>
              {draft.color.toLowerCase() !== DEFAULT_COLOR.toLowerCase() && (
                <button
                  type="button"
                  onClick={() => set('color', DEFAULT_COLOR)}
                  className="text-xs font-semibold text-info hover:underline"
                >
                  Reset to default
                </button>
              )}
            </div>
            {/* Preset swatches for a quick pick; the dashed "+" opens the full
                native picker for any custom color; the hex field mirrors the
                value + accepts a pasted code. */}
            <div className="flex flex-wrap items-center gap-2">
              {PRESET_COLORS.map((c) => {
                const active = draft.color.toLowerCase() === c.toLowerCase();
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => set('color', c)}
                    aria-label={`Color ${c}`}
                    aria-pressed={active}
                    className={`h-8 w-8 rounded-full border-2 transition ${active ? 'border-dark ring-2 ring-dark/20' : 'border-dark/10 hover:border-dark/30'}`}
                    style={{ backgroundColor: c }}
                  />
                );
              })}
              <label
                className="relative flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-2 border-dashed border-dark/30 text-dark/50 hover:border-dark/50"
                title="Custom color"
              >
                <Plus className="h-4 w-4" />
                <input
                  type="color"
                  value={draft.color}
                  onChange={(e) => set('color', e.target.value)}
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                  aria-label="Custom color"
                />
              </label>
            </div>
            <Input
              value={draft.color}
              onChange={(e) => set('color', e.target.value)}
              className="font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <Label>
              Prefilled message{' '}
              <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Input
              value={draft.message}
              onChange={(e) => set('message', e.target.value)}
              placeholder="Hi! I have a question"
            />
          </div>

          <label
            className={cn(
              'flex items-start gap-3',
              isFree ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
            )}
          >
            <Checkbox
              checked={badgeShown}
              disabled={isFree}
              onCheckedChange={(c) => set('show_badge', c === true)}
              className="mt-0.5"
            />
            <span className="flex-1">
              <span className="block text-sm font-semibold text-dark">
                Show "Powered by HeyMotek"
              </span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                {isFree
                  ? 'Upgrade to Pro to remove the badge.'
                  : 'A small credit under the button.'}
              </span>
            </span>
          </label>
        </div>

        {/* Preview */}
        <div className="space-y-1.5">
          <Label>Preview</Label>
          <WidgetPreview
            color={draft.color}
            position={draft.position}
            showBadge={badgeShown}
          />
        </div>
      </div>

      {error && <Banner variant="error">{error}</Banner>}

      <Button
        onClick={save}
        disabled={!dirty || saving}
        className="w-full rounded-full bg-brand py-3.5 text-base font-bold text-white hover:bg-brand-dark sm:w-auto sm:px-10"
      >
        {saving ? 'saving...' : 'Save'}
      </Button>
    </div>
  );
}

function WidgetPreview({
  color,
  position,
  showBadge,
}: {
  color: string;
  position: 'bottom-right' | 'bottom-left';
  showBadge: boolean;
}) {
  const left = position === 'bottom-left';
  return (
    <div className="relative h-44 overflow-hidden rounded-2xl border border-dark/10 bg-bg-tint">
      <div className="space-y-2 p-4">
        <div className="h-2.5 w-3/4 rounded bg-dark/10" />
        <div className="h-2.5 w-1/2 rounded bg-dark/10" />
        <div className="h-2.5 w-2/3 rounded bg-dark/10" />
      </div>
      <div
        className={cn(
          'absolute bottom-3 flex flex-col gap-1',
          left ? 'left-3 items-start' : 'right-3 items-end',
        )}
      >
        <WhatsAppGlyphButton color={color} className="h-12 w-12 shadow-lg" />
        {showBadge && (
          <span className="text-[10px] font-medium text-muted-foreground">
            heymotek
          </span>
        )}
      </div>
    </div>
  );
}
